import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { profileApi } from '../profile/profileApi';
import { publicCollectionApi } from '../collection/publicCollectionApi';
import { tradeApi } from '../trade/tradeApi';
import { activityApi } from '../activity/activityApi';
import {
  MyBlocksResponse,
  BlockUserArgs,
  UnblockUserArgs,
} from 'types/interfaces/block';

// RTK tags only invalidate within one api instance, so block/unblock must
// explicitly refresh the profile, collection, sendcards, and activity caches
// that the backend now filters server-side.
const invalidateBlockAffectedCaches = (
  dispatch: (action: unknown) => unknown,
  username: string
) => {
  dispatch(
    profileApi.util.invalidateTags([
      { type: 'PublicProfile', id: username },
      { type: 'ProfileStats', id: username },
      { type: 'ProfileTradeHistory', id: username },
    ])
  );
  dispatch(
    publicCollectionApi.util.invalidateTags([
      { type: 'PublicHaveList', id: username },
      { type: 'PublicWantList', id: username },
    ])
  );
  dispatch(tradeApi.util.invalidateTags(['TradePackages', 'MatchIndex']));
  dispatch(activityApi.util.invalidateTags(['RecentActivity', 'AvailableFeed']));
};

export const blockApi = createApi({
  reducerPath: 'blockApi',
  baseQuery,
  tagTypes: ['MyBlocks'],
  endpoints: (builder) => ({
    blockUser: builder.mutation<{ blocked: true }, BlockUserArgs>({
      query: ({ userId, reason }) => ({
        url: `/users/${userId}/block`,
        method: 'POST',
        body: { reason },
        requiresAuth: true,
      }),
      invalidatesTags: ['MyBlocks'],
      async onQueryStarted({ username }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateBlockAffectedCaches(dispatch, username);
        } catch {
          // Failed block: nothing to invalidate
        }
      },
    }),

    unblockUser: builder.mutation<{ blocked: false }, UnblockUserArgs>({
      query: ({ userId }) => ({
        url: `/users/${userId}/block`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['MyBlocks'],
      async onQueryStarted({ username }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateBlockAffectedCaches(dispatch, username);
        } catch {
          // Failed unblock: nothing to invalidate
        }
      },
    }),

    getMyBlocks: builder.query<MyBlocksResponse, { page?: number; limit?: number } | void>({
      query: (args) => ({
        url: `/users/me/blocks?page=${args?.page ?? 1}&limit=${args?.limit ?? 25}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['MyBlocks'],
    }),
  }),
});

export const {
  useBlockUserMutation,
  useUnblockUserMutation,
  useGetMyBlocksQuery,
} = blockApi;
